import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {ImageData, VideoData} from '@Libraries/add-media-library';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';

interface CreatePostersConfig {
  opts: CreateDesignFromMediaModalOpts;
  allCreatedPosterIds: string[];
  failedUploads: File[];
  accumulatedItemsForMultipage: (ImageData | VideoData)[];
}

interface SharedPosterCreationLogic {
  onAddSelected: (items: (ImageData | VideoData)[], currentUploadIndex?: number, totalUploadsCount?: number) => Promise<void>;
  onErrorDuringUpload: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
}

const getSharedPosterCreationLogic = ({opts, allCreatedPosterIds, failedUploads, accumulatedItemsForMultipage}: CreatePostersConfig): SharedPosterCreationLogic => {
  const createSinglePagePostersFromItems = async (items: (ImageData | VideoData)[], currentIndex?: number, totalPosterCreationsCount?: number): Promise<void> => {
    const posterCreationPromises: Promise<void>[] = [];

    showLoading(`createDesignFromUpload`, {text: window.i18next.t('pmwjs_creating')});
    for (let index = 0; index < items.length; index++) {
      posterCreationPromises.push(createSinglePagePosterFromItem(items[index], currentIndex ?? index + 1, totalPosterCreationsCount ?? items.length));
    }

    await Promise.all(posterCreationPromises);
    hideLoading(`createDesignFromUpload`);

    if (allCreatedPosterIds.length + failedUploads.length === (totalPosterCreationsCount ?? items.length)) {
      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    }
  };

  const createMultipagePosterFromItems = async (items: (ImageData | VideoData)[]): Promise<void> => {
    showLoading(`createDesignFromUpload`, {text: window.i18next.t('pmwjs_creating')});

    try {
      const itemsData = items.map((item) => ({
        type: item.type,
        hashedFilename: item.hashedFilename,
      }));

      const newPosterHashedId = (await window.PMW.writeLocal('poster/createMultipagePosterForMediaItems', {
        itemsData: JSON.stringify(itemsData),
        fid: opts.idFolder,
      })) as string;

      opts.onPosterCreated({
        newlyCreatedPosterHashedId: newPosterHashedId,
        currentIndex: 1,
        totalPosterCreationsCount: 1,
      });
      allCreatedPosterIds.push(newPosterHashedId);

      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    } catch (error) {
      console.error(error);
      openErrorModal();
    } finally {
      hideLoading(`createDesignFromUpload`);
    }
  };

  const createSinglePagePosterFromItem = async (item: ImageData | VideoData, currentIndex?: number, totalPosterCreationsCount?: number): Promise<void> => {
    try {
      const newPosterHashedId = (await window.PMW.writeLocal('poster/createPosterForMediaItem', {
        type: item.type,
        hashedFilename: item.hashedFilename,
        fid: opts.idFolder,
      })) as string;

      opts.onPosterCreated({
        newlyCreatedPosterHashedId: newPosterHashedId,
        currentIndex,
        totalPosterCreationsCount,
      });
      allCreatedPosterIds.push(newPosterHashedId);
    } catch (error) {
      failedUploads.push({name: `Failed to create poster for ${item.type} with hashedFilename ${item.hashedFilename}`} as File);
      console.error(error);
      openErrorModal();
    }
  };

  const onAddSelected = async (items: (ImageData | VideoData)[], currentUploadIndex?: number, totalUploadsCount?: number): Promise<void> => {
    if (!isUserLoggedIn()) {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'default',
        onLoginSuccess: async () => {
          await handlePosterCreation(items, currentUploadIndex, totalUploadsCount);
        },
      });
      return;
    }

    await handlePosterCreation(items, currentUploadIndex, totalUploadsCount);
  };

  const handlePosterCreation = async (items: (ImageData | VideoData)[], currentUploadIndex?: number, totalUploadsCount?: number): Promise<void> => {
    const shouldCreateMultipage = opts.allowMultipagePosterCreation;

    if (!shouldCreateMultipage) {
      await createSinglePagePostersFromItems(items, currentUploadIndex, totalUploadsCount);
      return;
    }

    const areMultipleUploadsInProgress = totalUploadsCount !== undefined && totalUploadsCount > 1;

    if (areMultipleUploadsInProgress) {
      accumulatedItemsForMultipage.push(...items);

      if (accumulatedItemsForMultipage.length + failedUploads.length === totalUploadsCount) {
        await createMultipagePosterFromItems(accumulatedItemsForMultipage);
      }
      return;
    }

    await createMultipagePosterFromItems(items);
  };

  const onErrorDuringUpload = (file: File, currentUploadIndex?: number, totalItemsToUpload?: number): void => {
    failedUploads.push(file);

    if (opts.onErrorDuringUpload) {
      opts.onErrorDuringUpload(file, currentUploadIndex, totalItemsToUpload);
    }

    if (totalItemsToUpload !== undefined && allCreatedPosterIds.length + failedUploads.length === totalItemsToUpload) {
      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    }
  };

  return {onAddSelected, onErrorDuringUpload};
};

window.PMW.mergePMW(PMW, {
  triggerCreateDesignFromUserUpload(opts: CreateDesignFromMediaModalOpts) {
    const allCreatedPosterIds: string[] = [];
    const failedUploads: File[] = [];
    const accumulatedItemsForMultipage: (ImageData | VideoData)[] = [];

    const {onAddSelected, onErrorDuringUpload} = getSharedPosterCreationLogic({
      opts,
      allCreatedPosterIds,
      failedUploads,
      accumulatedItemsForMultipage,
    });

    if (opts.onTriggered) {
      opts.onTriggered();
    }

    openUserMediaModal({
      onAddSelectedCTAText: opts.footerPrimaryCtaText ?? window.i18next.t('pmwjs_create_design'),
      maxSelectionNumber: opts.maxSelectionNumber,
      onAddSelected,
      defaultTab: UploadsMediaType.IMAGE,
      mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO],
      uploadAcceptedMimetype: 'image/*,video/*',
      onStartAddSelected: opts.onPosterCreationsStarted,
      onUploadError: onErrorDuringUpload,
      isForDesignUpload: true,
      hideFooterSecondaryActions: opts.hideFooterSecondaryActions,
    });
  },

  triggerCreateDesignFromAiImage(opts: CreateDesignFromMediaModalOpts) {
    const allCreatedPosterIds: string[] = [];
    const failedUploads: File[] = [];
    const accumulatedItemsForMultipage: (ImageData | VideoData)[] = [];

    const {onAddSelected} = getSharedPosterCreationLogic({
      opts,
      allCreatedPosterIds,
      failedUploads,
      accumulatedItemsForMultipage,
    });

    if (opts.onTriggered) {
      opts.onTriggered();
    }

    openStockMediaModal({
      onStartAddSelected: opts.onPosterCreationsStarted,
      onAddSelected: onAddSelected,
      maxSelectionNumber: opts.maxSelectionNumber,
      tabsToShow: [StockPanelTabs.TEXT_TO_IMAGE],
      defaultSearchTab: StockPanelTabs.TEXT_TO_IMAGE,
      showTextToImageTool: true,
    });
  },
});
